import { useCallback, useEffect, useRef, useState } from 'react';

// WHERE this browser was in the manual it had open: which phase's section was on screen,
// which part was open inside it, which of a phase's views.
//
// The viewer keeps one phase mounted at a time and each phase opens on its own first
// thing, so a refresh landed you on the PDF with the work a dozen clicks away — and
// reaching it again meant picking the phase, then the part, then the view. Phase 2 of a
// chair takes a while and is exactly the kind of page you reload.
//
// localStorage, beside notice.lastDocId and notice.viewerMode, and for the same reason:
// where THIS window was looking is not a fact about the manual. On the server it would
// drag a second admin's viewer around while they worked.
//
// ONE MANUAL'S WORTH, replaced whole when a different one is opened. It is a resume point
// for the refresh you just did, not a history: a part chosen in another manual is not a
// place in this one, and opening a different notice starts where it always did — at the
// top, on the first part. That is also what keeps this from growing without bound.
//
// Every slot is a plain string and nobody validates it here. What a slot MEANS is the
// caller's, and each of them already has to survive an answer it does not recognise —
// a phase id from a pipeline that has changed, a part key from a parts list that has been
// read again. They all fall back rather than break, so a stale place costs nothing.
const KEY = 'notice.lastPlace';

type Store = { docId: string; at: Record<string, string> };

function read(): Store | null {
  try {
    const s = JSON.parse(localStorage.getItem(KEY) || 'null') as Store | null;
    return s && typeof s.docId === 'string' && s.at && typeof s.at === 'object' ? s : null;
  } catch {
    // Written by a version that shaped it differently, or by hand. There is no answer to
    // give and no news in saying so: everything opens where it opened before any of this.
    return null;
  }
}

export function readPlace(docId: string, slot: string): string | null {
  const s = read();
  return s && s.docId === docId ? s.at[slot] ?? null : null;
}

export function writePlace(docId: string, slot: string, value: string) {
  if (!docId) return;
  const s = read();
  const at = s && s.docId === docId ? { ...s.at, [slot]: value } : { [slot]: value };
  localStorage.setItem(KEY, JSON.stringify({ docId, at } satisfies Store));
}

// One remembered place, as a piece of state. '' is "nothing remembered", which every
// caller already has a meaning for — the first part, the default view.
//
// The write happens in the SETTER and not in an effect, which is the whole reason this is
// a hook rather than two calls. An effect on the value would fire once with the previous
// manual's place under the new manual's id, on the render between the document changing
// and this noticing.
const listeners = new Set<(docId: string, slot: string, value: string) => void>();

export function usePlace(docId: string, slot: string): [string, (v: string) => void] {
  const [value, setValue] = useState(() => readPlace(docId, slot) ?? '');
  // Which manual `value` is a place in. The phases stay mounted across a change of
  // document — they are hidden, not unmounted — so nothing here remounts to re-read.
  const shownFor = useRef(docId);
  useEffect(() => {
    if (shownFor.current === docId) return;
    shownFor.current = docId;
    setValue(readPlace(docId, slot) ?? '');
  }, [docId, slot]);

  // EVERY HOOK ON THE SAME SLOT HEARS IT: a place set in one view is the place another view of it
  // opens on — the joint tab picked is the joint the run's prompt window shows.
  useEffect(() => {
    const hear = (d: string, s: string, v: string) => { if (d === docId && s === slot) setValue(v); };
    listeners.add(hear);
    return () => { listeners.delete(hear); };
  }, [docId, slot]);

  const set = useCallback((v: string) => {
    writePlace(docId, slot, v);
    listeners.forEach((hear) => hear(docId, slot, v));
  }, [docId, slot]);

  return [value, set];
}
